import React, { useState, useMemo, useEffect } from "react";
import "./BankStatementDashboard.scss";
import {
  getBankReportData,
  getBureauReportData,
  getKycVerificationByUserIdNew,
  getStateForBankingByUserId,
} from "../../ActionCreator";
import Loader from "../../../../../../presentationals/Loader/Loader.component";

// Helper: format amount to ₹ with commas
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount);
};

// Helper: format date to DD-MMM-YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Helper: capitalize first letter
const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const BankStatementDashboard = ({ allDetail }) => {
  //
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [data, setData] = useState();
  const [selectedUserTransactionId, setSelectedUserTransactionId] = useState("");
  const [loader, setLoader] = useState(false);


  // State for transaction pagination & filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const rowsPerPage = 15;
  //
  const customer = data?.customerInfo || {};
  const accountAnalysis = data?.accountAnalysis?.[0] || {};
  const summary = accountAnalysis.summaryInfo || {};
  const monthlyDetails = accountAnalysis.monthlyDetails || [];
  const top5Received = accountAnalysis.top5FundsReceived || [];
  const top5Transferred = accountAnalysis.top5FundsTransferred || [];
  const fcu = accountAnalysis.fCUAnalysis || {};
  const possibleObligations = accountAnalysis.possibleObligations || [];
  const allTransactions = data?.accountXns?.[0]?.xns || [];

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return allTransactions.filter((tx) => {
      const matchesSearch =
        tx.narration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.chqNo?.toString().toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        categoryFilter === "All" || tx.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [allTransactions, searchTerm, categoryFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / rowsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  // Unique categories for filter dropdown
  const uniqueCategories = useMemo(() => {
    const cats = new Set(
      allTransactions.map((tx) => tx.category).filter(Boolean),
    );
    return ["All", ...Array.from(cats)];
  }, [allTransactions]);

  // Render summary KPI cards
  const renderKPICards = () => {
    const totals = summary.total || {};
    const averages = summary.average || {};
    return (
      <div className="kpi-grid">
        <div className="kpi-card">
          <span>Total Credits</span>
          <strong>{formatCurrency(totals.totalCredit)}</strong>
        </div>
        <div className="kpi-card">
          <span>Total Debits</span>
          <strong>{formatCurrency(totals.totalDebit)}</strong>
        </div>
        <div className="kpi-card">
          <span>Net Cash Flow</span>
          <strong>
            {formatCurrency(totals.totalCredit - totals.totalDebit)}
          </strong>
        </div>
        <div className="kpi-card">
          <span>Avg Balance</span>
          <strong>{formatCurrency(averages.balAvg)}</strong>
        </div>
        <div className="kpi-card">
          <span>Cheque Deposits</span>
          <strong>{totals.chqDeposits}</strong>
          <small>{formatCurrency(totals.totalChqDeposit)}</small>
        </div>
        <div className="kpi-card">
          <span>Cheque Issues</span>
          <strong>{totals.chqIssues}</strong>
          <small>{formatCurrency(totals.totalChqIssue)}</small>
        </div>
        <div className="kpi-card">
          <span>Loan EMIs</span>
          <strong>{totals.emiOrLoans}</strong>
          <small>{formatCurrency(totals.totalEmiOrLoan)}</small>
        </div>
        <div className="kpi-card">
          <span>Transaction Count</span>
          <strong>{totals.xnsCount}</strong>
        </div>
      </div>
    );
  };

  useEffect(() => {
    getAllUsers();
  }, []);

  const getAllUsers = () => {
    getKycVerificationByUserIdNew(
      allDetail?.userId,
      (res) => {
        const usersData = res?.data || {};

        const allUsers = Object.entries(usersData)
          .filter(([_, users]) => Array.isArray(users))
          .flatMap(([userType, users]) =>
            users.map((user) => ({
              ...user,
              userType,
            })),
          );

        setAllUsers(allUsers);
        if (allUsers.length > 0) {
          setSelectedUserId(allUsers[0].userId);
        }
      },
      (err) => {
        console.log("Errpr to get KYC", err);
      },
    );
  };

  const getSelectedState = (userId) => {
    getStateForBankingByUserId(
      userId,
      (res) => {
        if(res?.success === true){
        setSelectedUserTransactionId(res?.data?.transaction?.clientTransactionId)
        }
      },
      (err) => {
     
      },
    );
  };

  const getBankData = (selectedUserTransactionId) => {
    if (!selectedUserTransactionId) return;
    setLoader(true);
    getBankReportData(
      selectedUserTransactionId,
      (callback) => {
        setLoader(false);
        if (callback?.success === true) {
          setData(callback?.data);
        }else{
          setData(null)
        }
      },
      (error) => {
        setLoader(false);
       
      },
    );
  };

  const handleUserClick = (userId) => {
    setSelectedUserId(userId);
  };

  useEffect(() => {
    if (selectedUserId) {
      getSelectedState(selectedUserId);
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (selectedUserTransactionId) {
      getBankData(selectedUserTransactionId);
    }
  }, [selectedUserTransactionId]);

  return (
    <div className="dashboard-container">
      {/* Customer Info Header */}

      <div
        style={{
          display: "flex",
          gap: "10px",
        }}
      >
        {allUsers?.map((item) => (
          <div
            key={item.userId}
            onClick={() => handleUserClick(item.userId)}
            style={{
              cursor: "pointer",
              border:
                selectedUserId === item.userId
                  ? "2px solid #2563eb"
                  : "1px solid #d1d5db",
              backgroundColor:
                selectedUserId === item.userId ? "#eff6ff" : "#ffffff",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "12px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
              transition: "0.2s",
            }}
          >
            <h3
              style={{
                marginBottom: "10px",
                marginTop: "0",
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              {capitalizeFirstLetter(item?.userType === 'BORROWER' ? item?.businessName : item?.fullName) || "N/A"}
            </h3>

            {/* <p>
            <strong>Name:</strong> {item.fullName || "N/A"}
          </p> */}

            <p>
              <strong>User Type:</strong> {item.userType}
            </p>
          </div>
        ))}
      </div>
      {
        loader ? (
          <Loader />
        ) : (
          <>
          <section className="customer-section">
        <h2>Customer Information</h2>
        <div className="customer-details">
          <p>
            <strong>Name:</strong> {customer.name}
          </p>
          <p>
            <strong>Email:</strong> {customer.email}
          </p>
          <p>
            <strong>Bank:</strong> {customer.bank}
          </p>
          <p>
            <strong>Transaction ID:</strong> {customer.perfiosTransactionId}
          </p>
          <p>
            <strong>Address:</strong> {customer.address}
          </p>
        </div>
      </section>

      {/* Summary KPIs */}
      <section className="summary-section">
        <h2>Financial Summary</h2>
        {renderKPICards()}
      </section>

      {/* Monthly Details Table */}
      <section className="monthly-section">
        <h2>Monthly Breakdown</h2>
        <div className="table-responsive">
          <table className="monthly-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Start Date</th>
                <th>Avg Balance</th>
                <th>Credits</th>
                <th>Debits</th>
                <th>Chq Deposits</th>
                <th>Chq Issues</th>
                <th>EMIs/Loans</th>
              </tr>
            </thead>
            <tbody>
              {monthlyDetails.map((month, idx) => (
                <tr key={idx}>
                  <td>{month.monthName}</td>
                  <td>{formatDate(month.startDate)}</td>
                  <td className={month.balAvg < 0 ? "negative" : ""}>
                    {formatCurrency(month.balAvg)}
                  </td>
                  <td>{month.credits}</td>
                  <td>{month.debits}</td>
                  <td>{month.chqDeposits}</td>
                  <td>{month.chqIssues}</td>
                  <td>{month.emiOrLoans}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Top Funds Received / Transferred */}
      <div className="top-funds-grid">
        <section className="funds-card">
          <h3>Top Funds Received</h3>
          <table className="simple-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Category</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {top5Received.map((item, i) => (
                <tr key={i}>
                  <td>{item.month}</td>
                  <td>{item.category}</td>
                  <td>{formatCurrency(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="funds-card">
          <h3>Top Funds Transferred</h3>
          <table className="simple-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Category</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {top5Transferred.map((item, i) => (
                <tr key={i}>
                  <td>{item.month}</td>
                  <td>{item.category}</td>
                  <td>{formatCurrency(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      {/* FCU Analysis */}
      <section className="fcu-section">
        <h2>FCU Analysis</h2>
        <p>
          <strong>Fraud Score:</strong> {fcu.fcuScore || "N/A"}
        </p>
        {fcu.possibleFraudIndicators && (
          <>
            <details>
              <summary>
                ⚠️ IMPS Transactions Above Threshold (
                {fcu.possibleFraudIndicators.impsAboveThresholdXns?.length || 0}
                )
              </summary>
              <div className="table-responsive">
                <table className="txn-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Narration</th>
                      <th>Amount</th>
                      <th>Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(
                      fcu.possibleFraudIndicators.impsAboveThresholdXns || []
                    ).map((tx, i) => (
                      <tr key={i}>
                        <td>{formatDate(tx.date)}</td>
                        <td>{tx.narration.slice(0, 60)}...</td>
                        <td className={tx.amount < 0 ? "negative" : ""}>
                          {formatCurrency(tx.amount)}
                        </td>
                        <td>{tx.category}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
            <details>
              <summary>
                🔁 Irregular EMI/ECS (
                {fcu.possibleFraudIndicators.behaviouralTransactionalIndicators
                  ?.irregularEmiEcsXns?.length || 0}
                )
              </summary>
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Narration</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(
                      fcu.possibleFraudIndicators
                        .behaviouralTransactionalIndicators
                        ?.irregularEmiEcsXns || []
                    ).map((tx, i) => (
                      <tr key={i}>
                        <td>{formatDate(tx.date)}</td>
                        <td>{tx.narration.slice(0, 50)}</td>
                        <td className={tx.amount < 0 ? "negative" : ""}>
                          {formatCurrency(tx.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
            <details>
              <summary>💸 NEFT/RTGS Avg Monthly Balance</summary>
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Narration</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(
                      fcu.possibleFraudIndicators
                        .behaviouralTransactionalIndicators
                        ?.neftRtgsGreaterThanAMB || []
                    )
                      .slice(0, 20)
                      .map((tx, i) => (
                        <tr key={i}>
                          <td>{formatDate(tx.date)}</td>
                          <td>{tx.narration.slice(0, 60)}</td>
                          <td className={tx.amount < 0 ? "negative" : ""}>
                            {formatCurrency(tx.amount)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </details>
            <p>
              <strong>Unusual OD/CC Activity:</strong>{" "}
              {fcu.possibleFraudIndicators.behaviouralTransactionalIndicators
                ?.unusualODCCActivity?.status === "true"
                ? "🚨 Detected"
                : "Normal"}
            </p>
          </>
        )}
      </section>

      {/* Possible Obligations */}
      <section className="obligations-section">
        <h2>Possible Obligations (Loans / EMI)</h2>
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Cheque No</th>
                <th>Narration</th>
                <th>Amount</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {possibleObligations.map((ob, i) => (
                <tr key={i}>
                  <td>{formatDate(ob.date)}</td>
                  <td>{ob.chqNo || "-"}</td>
                  <td>{ob.narration.slice(0, 50)}</td>
                  <td className="negative">{formatCurrency(-ob.amount)}</td>
                  <td>{formatCurrency(ob.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Transactions List with Pagination & Filter */}
      <section className="transactions-section">
        <h2>All Transactions ({allTransactions.length})</h2>
        <div className="filter-bar">
          <input
            type="text"
            placeholder="Search by narration or cheque no..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            {uniqueCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
        <div className="table-responsive">
          <table className="txn-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Cheque No</th>
                <th>Narration</th>
                <th>Amount</th>
                <th>Balance</th>
                <th>Mode</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTransactions.map((tx, idx) => (
                <tr key={idx}>
                  <td>{formatDate(tx.date)}</td>
                  <td>{tx.chqNo || "-"}</td>
                  <td className="narration-cell">{tx.narration}</td>
                  <td className={tx.amount < 0 ? "negative" : ""}>
                    {formatCurrency(tx.amount)}
                  </td>
                  <td>{formatCurrency(tx.balance)}</td>
                  <td>{tx.mox || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </section>
          </>
        )
      }
      
    </div>
  );
};

export default BankStatementDashboard;
